import { formatDate, getInitials } from '@/pages/projectList/utils'
import { Badge, Box, Circle, Flex, Icon, IconButton, Input, Link, Text, chakra } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { UserProfileForAdmin } from '@infraloop/services'
import { LuBuilding2, LuExternalLink, LuRotateCcw } from 'react-icons/lu'
import { Link as RouterLink } from 'react-router'

import { USER_LIST_COLUMN_GAP, USER_LIST_GRID_COLUMNS } from '../layout'
import {
  buildStripeCustomerUrl,
  buildUserDashboardUrl,
  getAvatarColor,
  getUserFullName,
  getUserMetaLine,
} from '../utils'

/** Chakra-styled react-router link, so navigating to a user's dashboard is client-side. */
const NavLink = chakra(RouterLink)

const UserRow = ({
  user,
  editMode,
  changedHubspotId,
  onHubspotIdChange,
  onClearHubspotId,
}: {
  /** The user to render. */
  user: UserProfileForAdmin
  /** Whether HubSpot-ID edit mode is active (renders an input instead of text). */
  editMode: boolean
  /** Pending edited HubSpot ID for this user, if changed from the saved value. */
  changedHubspotId?: string
  /** Update this row's pending HubSpot ID. */
  onHubspotIdChange: (userId: string, value: string) => void
  /** Discard this row's pending HubSpot-ID change. */
  onClearHubspotId: (userId: string) => void
}) => {
  const { t } = useTranslation()

  const seed = user.auth0_email ?? user.user_id
  const fullName = getUserFullName(user)
  const metaLine = getUserMetaLine(user)
  const hubEmail = user.hubspot_properties?.email
  const isDirty = changedHubspotId !== undefined
  const hubspotIdValue = changedHubspotId ?? user.hubspot_id ?? ''
  // Carry this user's storage + email to the dashboard so its gauge reflects them (not the
  // admin) and the "viewing another user" banner can name them — both are already on the
  // fetched profile, so no extra request is needed.
  const dashboardNavState = { storageUsed: user.stats?.total_size ?? 0, userEmail: user.auth0_email }

  return (
    <Box
      display="grid"
      gridTemplateColumns={USER_LIST_GRID_COLUMNS}
      alignItems="center"
      columnGap={USER_LIST_COLUMN_GAP}
      px="18px"
      py="14px"
      borderBottom="1px solid"
      borderColor="gray.100"
      _hover={{ bg: '#F8FAFD' }}
      transition="background .1s"
    >
      {/* User identity */}
      <Flex align="center" gap="11px" minW={0}>
        <Circle size="36px" bg={getAvatarColor(seed)} color="white" fontSize="12px" fontWeight="bold" flexShrink={0}>
          {getInitials(fullName || user.auth0_email)}
        </Circle>
        <Box minW={0}>
          <NavLink
            to={buildUserDashboardUrl(user.user_id)}
            state={dashboardNavState}
            display="block"
            fontSize="14px"
            fontWeight="semibold"
            color="gray.800"
            textDecoration="none"
            _hover={{ color: 'primary.600' }}
            truncate
          >
            {user.auth0_email}
          </NavLink>
          <Text fontSize="12px" color="gray.500" mt="2px" truncate>
            {metaLine || t('hatsuly.userList.noInfo')}
          </Text>
        </Box>
      </Flex>

      {/* HubSpot cluster */}
      <Box minW={0}>
        {hubEmail ? (
          <NavLink
            to={buildUserDashboardUrl(user.user_id)}
            state={dashboardNavState}
            display="block"
            fontSize="13px"
            color="primary.600"
            textDecoration="none"
            truncate
          >
            {hubEmail}
          </NavLink>
        ) : (
          <Flex as="span" align="center" gap="5px" fontSize="12px" color="gray.500">
            <Box as="span" w="6px" h="6px" borderRadius="full" bg="gray.300" />
            {t('hatsuly.userList.noHubspotEmail')}
          </Flex>
        )}

        <Flex align="center" gap="7px" mt="6px">
          {editMode ? (
            <>
              <Input
                value={hubspotIdValue}
                onChange={(e) => onHubspotIdChange(user.user_id, e.target.value)}
                placeholder={t('hatsuly.userList.hubspotIdPlaceholder')}
                w="160px"
                size="sm"
                fontFamily="mono"
                bg={isDirty ? '#FFFCF4' : 'white'}
                borderColor={isDirty ? 'orange.400' : 'gray.200'}
              />
              {isDirty && (
                <>
                  <IconButton
                    aria-label={t('hatsuly.userList.undoChange')}
                    title={t('hatsuly.userList.undoChange')}
                    size="xs"
                    variant="ghost"
                    color="orange.600"
                    _hover={{ bg: 'orange.100' }}
                    onClick={() => onClearHubspotId(user.user_id)}
                  >
                    <Icon as={LuRotateCcw} boxSize="15px" />
                  </IconButton>
                  <Badge colorPalette="orange" size="sm">
                    {t('hatsuly.userList.unsaved')}
                  </Badge>
                </>
              )}
            </>
          ) : (
            <Text
              fontSize="13px"
              fontFamily={user.hubspot_id ? 'mono' : undefined}
              color={user.hubspot_id ? 'gray.800' : 'gray.300'}
            >
              {user.hubspot_id || t('hatsuly.userList.noHubspotId')}
            </Text>
          )}
        </Flex>
      </Box>

      {/* Subscription */}
      <Box>
        <Text fontSize="14px" fontWeight="semibold" color="gray.800">
          {formatDate(user.base_feature_end_at)}
        </Text>
        <Flex align="center" gap="6px" mt="5px">
          <Badge variant="outline" size="sm" color="gray.500">
            {t('hatsuly.userList.legacyPlan', { plan: user.subscriptions?.type ?? 'none' })}
          </Badge>
        </Flex>
      </Box>

      {/* Billing / agency */}
      <Box minW={0}>
        {user.stripe_id ? (
          <Link
            href={buildStripeCustomerUrl(user.stripe_id)}
            target="_blank"
            rel="noopener noreferrer"
            display="inline-flex"
            alignItems="center"
            gap="6px"
            color="primary.600"
            maxW="100%"
          >
            <Icon as={LuExternalLink} boxSize="13px" color="gray.500" flexShrink={0} />
            <Text as="span" fontSize="12px" fontFamily="mono" truncate>
              {user.stripe_id}
            </Text>
          </Link>
        ) : (
          <Text fontSize="13px" color="gray.300">
            —
          </Text>
        )}
        {user.reseller_name && (
          <Flex align="center" gap="6px" mt="6px">
            <Icon as={LuBuilding2} boxSize="12px" color="gray.500" flexShrink={0} />
            <Text fontSize="12px" color="gray.500" truncate>
              {user.reseller_name}
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  )
}

export default UserRow
